import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  useTheme,
  Divider,
  Avatar,
  Stack,
} from '@mui/material';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Scatter,
  ScatterChart,
  ZAxis,
  AreaChart,
  Area,
  ComposedChart,
} from 'recharts';
import { 
  TrendingUp, 
  Warning, 
  LocalHospital, 
  AccessibilityNew, 
  Elderly,
  MonetizationOn,
  MedicalServices,
  Home,
  DirectionsWalk,
  HealthAndSafety
} from '@mui/icons-material';

const ModuleDiagrams = () => {
  const theme = useTheme();

  // Fall Risk Statistics
  const fallRiskData = [
    { name: 'Age 65-74', value: 28 },
    { name: 'Age 75-84', value: 35 },
    { name: 'Age 85+', value: 37 },
  ];

  // Fall Consequences
  const consequencesData = [
    { name: 'Minor Injuries', value: 45 },
    { name: 'Moderate Injuries', value: 30 },
    { name: 'Severe Injuries', value: 15 },
    { name: 'Hospitalizations', value: 10 },
  ];

  // Fall Prevention Success Rate
  const preventionData = [
    { month: 'Jan', success: 65 },
    { month: 'Feb', success: 70 },
    { month: 'Mar', success: 75 },
    { month: 'Apr', success: 82 },
    { month: 'May', success: 88 },
    { month: 'Jun', success: 92 },
  ];

  const COLORS = [
    theme.palette.primary.main,
    theme.palette.secondary.main,
    theme.palette.success.main,
    theme.palette.warning.main,
  ];

  return (
    <Box sx={{ p: 2 }}>
      <Grid container spacing={3}>
        {/* Fall Risk by Age Group */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Fall Risk Distribution by Age Group (%)
              </Typography>
              <Typography variant="body1" paragraph>
                Understanding fall risk distribution across age groups is essential for targeted prevention strategies. This
                visualization presents the percentage of individuals at risk in different age brackets, based on comprehensive
                assessments. The data clearly shows an increasing risk pattern with age, emphasizing the need for early
                intervention and age-appropriate prevention strategies.
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Age-Related Considerations:
                • 65-74 age group shows emerging risk factors
                • 75-84 group demonstrates significant risk increase
                • 85+ group requires most intensive prevention measures
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={fallRiskData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, value }) => `${name}: ${value}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {fallRiskData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Fall Consequences Distribution */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Fall Consequences Distribution (%)
              </Typography>
              <Typography variant="body1" paragraph>
                The impact of falls varies significantly, ranging from minor injuries to severe complications requiring
                hospitalization. This chart categorizes fall consequences based on severity and frequency. Understanding this
                distribution helps in prioritizing prevention efforts and allocating resources effectively. The data emphasizes
                why prevention is crucial, as a significant portion of falls result in injuries requiring medical attention.
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Impact Analysis:
                • Minor injuries often indicate need for environmental modifications
                • Moderate injuries typically require rehabilitation
                • Severe injuries lead to extended recovery periods
                • Hospitalizations represent highest healthcare utilization
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={consequencesData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="value" fill={theme.palette.primary.main}>
                    {consequencesData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Prevention Success Rate */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Fall Prevention Success Rate Over Time (%)
              </Typography>
              <Typography variant="body1" paragraph>
                This trend analysis demonstrates the effectiveness of our comprehensive fall prevention program over time.
                The success rate incorporates multiple factors including reduced fall incidents, improved balance scores,
                and successful implementation of preventive measures. The steady improvement curve shows that consistent
                application of prevention strategies leads to better outcomes.
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Success Indicators:
                • Initial period shows rapid improvement from baseline
                • Mid-term results reflect program adherence
                • Long-term success demonstrates sustained benefits
                • 92% success rate achieved through consistent implementation
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={preventionData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis domain={[0, 100]} />
                  <Tooltip />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="success"
                    stroke={theme.palette.success.main}
                    strokeWidth={2}
                    name="Success Rate"
                  />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ModuleDiagrams;
